import type {
  OrderResponse,
  CancelResponse,
  SuccessResponse,
  OrderMessageContext,
  CancelMessageContext,
  LeverageMessageContext,
} from '../types';
import {
  formatFilledOrderMessage,
  formatRestingOrderMessage,
  formatBatchOrderMessage,
  formatCancelMessage,
  formatBatchCancelMessage,
  formatLeverageMessage,
  formatMarginModeMessage,
  formatTpSlMessage,
  cleanErrorMessage as cleanHLErrorMessage,
} from './message-formatter';
import { debugLog } from './logger';

/**
 * Parsed individual order/action status
 */
export interface ParsedOrderStatus {
  /** Success or error */
  status: 'success' | 'error';
  /** Order ID if successful */
  orderId?: number;
  /** Human-readable message */
  message?: string;
  /** Original filled data from Hyperliquid */
  filled?: { totalSz: string; avgPx: string; oid: number };
  /** Original resting data from Hyperliquid */
  resting?: { oid: number };
  /** Error message if failed */
  error?: string;
}

/**
 * Unified SDK response format
 * Works across all DEXs (Hyperliquid, Lighter, etc.)
 */
export interface ParsedResponse {
  /** Overall status: 'ok' if at least 1 succeeded, 'error' if all failed */
  status: 'ok' | 'error';
  /** Human-readable summary message */
  message: string;
  /** Detailed breakdown */
  response: {
    successCount: number;
    errorCount: number;
    totalCount: number;
    results: ParsedOrderStatus[];
  };
}

/**
 * Clean up Hyperliquid error messages for user display
 * Wrapper around message-formatter cleanErrorMessage
 */
function cleanErrorMessage(errorMessage: string): string {
  return cleanHLErrorMessage(errorMessage);
}

/**
 * Parse Hyperliquid order response into standardized format
 */
export function parseOrderResponse(
  hlResponse: OrderResponse,
  context?: OrderMessageContext
): ParsedResponse {
  debugLog('[SDK] ========== parseOrderResponse START ==========');
  debugLog('[SDK] Full hlResponse:', JSON.stringify(hlResponse, null, 2));

  const statuses = hlResponse.response.data.statuses;
  const results: ParsedOrderStatus[] = [];

  debugLog('[SDK] Statuses array:', statuses);
  debugLog('[SDK] Statuses type:', typeof statuses);
  debugLog('[SDK] Statuses isArray:', Array.isArray(statuses));
  debugLog('[SDK] Statuses length:', statuses?.length);
  debugLog('[SDK] Context:', JSON.stringify(context, null, 2));

  let successCount = 0;
  let errorCount = 0;

  debugLog('[SDK] Starting for loop over statuses...');
  for (const status of statuses) {
    debugLog('[SDK] ===== Processing status:', status);
    // Handle string statuses (e.g., "waitingForTrigger" for TP/SL orders)
    if (typeof status === 'string') {
      debugLog('[SDK] Processing string status:', status);
      if (status === 'waitingForTrigger') {
        // TP/SL trigger order successfully placed, waiting for trigger price
        const message = formatRestingOrderMessage(context);
        debugLog('[SDK] waitingForTrigger message:', message);
        results.push({
          status: 'success',
          message,
        });
        successCount++;
      } else {
        // Unknown string status - treat as error
        const cleanedError = cleanErrorMessage(status);
        results.push({
          status: 'error',
          message: cleanedError,
          error: cleanedError,
        });
        errorCount++;
      }
    } else if (status.error) {
      // Order failed - clean up error message for user display
      const cleanedError = cleanErrorMessage(status.error);
      results.push({
        status: 'error',
        message: cleanedError,
        error: cleanedError,
      });
      errorCount++;
    } else if (status.filled) {
      // Order filled immediately - use contextual message
      const message = formatFilledOrderMessage(
        status.filled.totalSz,
        status.filled.avgPx,
        context
      );
      results.push({
        status: 'success',
        orderId: status.filled.oid,
        message,
        filled: status.filled,
      });
      successCount++;
    } else if (status.resting) {
      // Order placed and resting on book - use contextual message
      const message = formatRestingOrderMessage(context);
      results.push({
        status: 'success',
        orderId: status.resting.oid,
        message,
        resting: status.resting,
      });
      successCount++;
    }
  }

  const totalCount = successCount + errorCount;

  // Determine overall status: ok if at least 1 succeeded, error if all failed
  const overallStatus: 'ok' | 'error' = successCount > 0 ? 'ok' : 'error';

  // Generate overall message
  let message: string;
  debugLog('[SDK] Selecting message... totalCount:', totalCount, 'grouping:', context?.grouping, 'hasTPSL:', context?.hasTPSL);

  if (totalCount === 1) {
    // Single order - use the specific result message
    message = results[0].message || 'Action completed';
    debugLog('[SDK] Single order message:', message);
  } else if (context?.hasTPSL && results.length > 0) {
    // TP/SL batch when opening new position - use the main order's message (first result)
    // The TP/SL message already includes "(with TP/SL)" indicator
    message = results[0].message || 'Action completed';
    debugLog('[SDK] normalTpsl batch message:', message);
  } else if (context?.grouping === 'positionTpsl' && results.length > 0) {
    // Setting TP/SL on existing position - use the TP/SL specific message
    message = results[0].message || 'Action completed';
    debugLog('[SDK] positionTpsl message:', message);
  } else {
    // Multiple orders - use batch message
    message = formatBatchOrderMessage(successCount, totalCount, context);
    debugLog('[SDK] Batch message:', message);
  }

  const parsedResponse = {
    status: overallStatus,
    message,
    response: {
      successCount,
      errorCount,
      totalCount,
      results,
    },
  };

  debugLog('[SDK] Final parsed response:', JSON.stringify(parsedResponse, null, 2));

  return parsedResponse;
}

/**
 * Parse Hyperliquid cancel response into standardized format
 */
export function parseCancelResponse(
  hlResponse: CancelResponse,
  context?: CancelMessageContext
): ParsedResponse {
  const statuses = hlResponse.response.data.statuses;
  const results: ParsedOrderStatus[] = [];

  let successCount = 0;
  let errorCount = 0;

  for (const status of statuses) {
    if (status === 'success') {
      const message = formatCancelMessage(context);
      results.push({
        status: 'success',
        message,
      });
      successCount++;
    } else {
      // status is error string - clean up for user display
      const cleanedError = cleanErrorMessage(status);
      results.push({
        status: 'error',
        message: cleanedError,
        error: cleanedError,
      });
      errorCount++;
    }
  }

  const totalCount = successCount + errorCount;
  const overallStatus: 'ok' | 'error' = successCount > 0 ? 'ok' : 'error';

  // Generate overall message
  let message: string;
  if (totalCount === 1) {
    // Single cancel - use the specific result message
    message = results[0].message || 'Action completed';
  } else {
    // Multiple cancels - use batch message
    message = formatBatchCancelMessage(successCount);
  }

  return {
    status: overallStatus,
    message,
    response: {
      successCount,
      errorCount,
      totalCount,
      results,
    },
  };
}

/**
 * Parse simple success response (leverage update, margin update, etc.)
 */
export function parseSuccessResponse(
  _hlResponse: SuccessResponse,
  actionName: string,
  context?: LeverageMessageContext
): ParsedResponse {
  // Generate contextual message based on action type
  let message: string;

  if (actionName === 'updateLeverage' || actionName === 'Update leverage') {
    message = formatLeverageMessage(context);
  } else if (actionName === 'updateMarginMode' || actionName === 'Update margin mode') {
    message = formatMarginModeMessage(context?.symbol, context?.isCross);
  } else if (actionName === 'setTpSl' || actionName === 'Set TP/SL') {
    message = formatTpSlMessage(context?.symbol);
  } else {
    // Fallback to generic message
    message = `${actionName} successful`;
  }

  return {
    status: 'ok',
    message,
    response: {
      successCount: 1,
      errorCount: 0,
      totalCount: 1,
      results: [
        {
          status: 'success',
          message,
        },
      ],
    },
  };
}
